import { Box, LinearProgress, Typography } from '@mui/material'
import { FC, useEffect } from 'react'
import { Achievement } from '../../../MissionService/missionsTypes'
import { CounterOverlay } from './CounterOverlay'

interface MissionProps {
    achievement?: Achievement
    animated?: boolean
}

export const ClaimedMission: FC<MissionProps> = ({ achievement, animated }) => {
    useEffect(() => {
        if (achievement && animated) {
            document.getElementById(`reward-${achievement.missionId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'center',
            })
        }
    }, [animated, achievement])

    return (
        <Box
            id={`reward-${achievement?.missionId}`}
            sx={{
                '@keyframes appear': {
                    '0%': { opacity: 0, transform: 'scale(0)' },
                    '70%': { opacity: 1, transform: 'scale(1.1)' },
                    '100%': { opacity: 1, transform: 'scale(1)' },
                },
                aspectRatio: '1/1',
                gridArea: 'icon',
                border: '2px solid',
                position: 'relative',
                borderColor: 'background.default',
                outline: (theme) =>
                    `2px solid ${achievement?.counter ? theme.palette.secondary.main : theme.palette.common.white}`,

                backgroundColor: 'background.default',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'hidden',

                '& > *:not(.progress-container):not(.rotate-animation):not(.counter-overlay)': {
                    animation: animated ? 'appear 0.75s ease-in-out forwards' : 'none',
                    transformOrigin: 'center',
                },
            }}
        >
            {achievement && achievement.counter >= 1 && achievement.repeatable && (
                <CounterOverlay value={achievement.counter} animated={animated} />
            )}

            {animated && (
                <Box
                    className="rotate-animation"
                    sx={{
                        zIndex: 1,
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        backgroundImage: `url(/assets/animations/rewards/rays.svg)`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        animation: 'rotate 10s linear infinite',

                        '@keyframes rotate': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(180deg)' },
                            // '100%': { transform: 'rotate(180deg) scale(0)' },
                        },
                    }}
                />
            )}

            {achievement ? (
                <Box
                    sx={{
                        borderRadius: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '12px',
                        zIndex: 40,
                        position: 'relative',
                        overflow: 'hidden',

                        '::after': {
                            content: '""',
                            position: 'absolute',
                            display: achievement.counter === 0 ? 'block' : 'none',
                            top: -4,
                            left: -4,
                            right: -4,
                            bottom: -4,
                            backgroundImage: `url('/assets/icons/lock-closed.svg')`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'center',
                            backgroundSize: '40%',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            zIndex: 40,
                        },
                    }}
                >
                    <img
                        src={achievement.assetUrl}
                        onError={(e) => {
                            if (achievement.fallbackAssetUrl) {
                                e.currentTarget.src = achievement.fallbackAssetUrl
                            }
                        }}
                        alt={achievement.title}
                        style={{
                            zIndex: 15,
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            filter: 'drop-shadow(1px 2px 2px black)',
                        }}
                    />
                </Box>
            ) : (
                <Typography variant="body2" textTransform="uppercase" textAlign="center">
                    No achievements earned yet
                </Typography>
            )}

            {achievement && achievement.tierProgress !== null && achievement.counter > 0 && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            display: 'block',
                            left: -4,
                            right: -4,
                            bottom: 8,
                            backgroundColor: 'secondary.main',
                            height: '2px',
                            zIndex: 100,
                        }}
                    />
                    <Box
                        className="progress-container"
                        sx={{
                            position: 'absolute',
                            bottom: '-4px',
                            left: '-2px',
                            right: '-2px',
                            padding: '0px',
                            zIndex: 199,
                            '*': {
                                borderRadius: 0,
                            },
                            '.MuiLinearProgress-root': {
                                height: '10px',
                                backgroundColor: 'background.default',
                                border: 'none',
                                outline: 'none',

                                '.MuiLinearProgress-bar': {
                                    margin: '2px',
                                    borderRadius: '12px',
                                    '::before': {
                                        top: 0,
                                        right: 0,
                                        width: `calc(${achievement.tierProgress}% + 2px)`,
                                        // width: '100%',
                                    },
                                    '::after': {
                                        bottom: 0,
                                        right: 0,
                                        width: `calc(${achievement.tierProgress}%)`,
                                        // width: '100%',
                                    },
                                },
                            },
                        }}
                    >
                        <LinearProgress
                            size="small"
                            variant="determinate"
                            color="primary"
                            value={achievement.tierProgress}
                            meter
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                position: 'absolute',
                                bottom: 1,
                                width: '100%',
                                color: '#FFF',
                                textAlign: 'center',

                                textShadow:
                                    '-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black, 0 -1px 0 black, 0 1px 0 black, -1px 0 0 black, 1px 0 0 black',

                                fontFamily: 'Space Grotesk',
                                fontSize: '12px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                zIndex: 200,
                            }}
                        >
                            {achievement.tierProgress}%
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    )
}
